import * as XLSX from 'xlsx';

const VAT_RATE = 0.2;

const FIXED_ROOM_COLUMNS = ['Bedrooms', 'Bathrooms', 'Toilets', 'Kitchens', 'extra rooms'];

const ROOM_KEY_MAP = {
  '1': 'Bedrooms',
  '2': 'Kitchens',
  '3': 'Bathrooms',
  '4': 'extra rooms',
  '5': 'Toilets',
};

const ROOM_ALIASES = [
  { column: 'Bedrooms', test: (name) => /\bbed(room|rooms)?s?\b/.test(name) },
  { column: 'Bathrooms', test: (name) => /\bbath(room|rooms)?s?\b/.test(name) },
  { column: 'Toilets', test: (name) => /\btoilet(s)?\b/.test(name) },
  { column: 'Kitchens', test: (name) => /\bkitchen(s)?\b/.test(name) },
  { column: 'extra rooms', test: (name) => /\bextra\s*room(s)?\b/.test(name) },
];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatMoney = (value) => Math.round(toNumber(value) * 100) / 100;

const cleanName = (value) =>
  String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeName = (value) => cleanName(value).toLowerCase();

const parseMaybeJson = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const propertyIdValue = (property, propertyId) =>
  property?.property_title || property?.ppty_id || property?.id || propertyId || '';

const propertyAddressValue = (property) =>
  [property?.apartment_number, property?.apartment_address]
    .filter(Boolean)
    .join(', ') ||
  property?.address ||
  property?.delivery_address_1 ||
  '';

const postcodeValue = (property) =>
  property?.delivery_zip_code || property?.postcode || property?.post_code || property?.postal_code || '';

const areaValue = (property) => {
  const raw =
    property?.area ??
    property?.property_area ??
    property?.sqm ??
    property?.square_meters ??
    property?.square_metres ??
    property?.size_sqm ??
    property?.size;

  if (raw === null || raw === undefined || raw === '') return '';
  const text = String(raw).trim();
  if (/sqm|sq\s*m|m2|m²/i.test(text)) return text;
  return `${text} sqm`;
};

const roomColumnForName = (name) => {
  const normalized = normalizeName(name);
  const match = ROOM_ALIASES.find((alias) => alias.test(normalized));
  return match?.column || null;
};

const getRoomQuantities = (property) => {
  const quantities = parseMaybeJson(property?.quantity);
  const rooms = Object.fromEntries(FIXED_ROOM_COLUMNS.map((column) => [column, '']));

  Object.entries(quantities).forEach(([key, value]) => {
    const directColumn = ROOM_KEY_MAP[String(key)];
    const aliasColumn = roomColumnForName(key);
    const column = directColumn || aliasColumn;
    if (column) rooms[column] = toNumber(value);
  });

  return rooms;
};

const itemQuantity = (item) => toNumber(item?.quantity ?? item?.qty);

const itemPrice = (item) => toNumber(item?.price);

const itemBaseName = (item) => cleanName(item?.name || item?.product_title || item?.title || 'Item');

const itemColumnName = (item) => {
  const name = itemBaseName(item);
  const price = itemPrice(item);

  if (!price || /\([0-9]+(\.[0-9]+)?\)\s*$/.test(name)) return name;

  return `${name} (${formatMoney(price)})`;
};

const getCartProperties = (cart, properties) => {
  const propertyMap = new Map((properties || []).map((property) => [String(property.id), property]));

  return Object.keys(cart || {}).map((propertyId) => ({
    propertyId,
    property: propertyMap.get(String(propertyId)) || {},
    items: Object.values(cart[propertyId] || {}),
  }));
};

export function exportCartToExcel({ cart, properties }) {
  const cartProperties = getCartProperties(cart, properties);
  if (!cartProperties.length) return false;

  const dynamicItemColumns = [];
  const dynamicItemColumnSet = new Set();

  cartProperties.forEach(({ items }) => {
    items.forEach((item) => {
      const fixedColumn = roomColumnForName(itemBaseName(item));
      if (fixedColumn) return;

      const column = itemColumnName(item);
      if (!column || dynamicItemColumnSet.has(column)) return;
      dynamicItemColumnSet.add(column);
      dynamicItemColumns.push(column);
    });
  });

  const headers = [
    'ppty ID',
    'ppty address',
    'ppty postcode',
    'Area',
    ...FIXED_ROOM_COLUMNS,
    ...dynamicItemColumns,
    'Price without VAT',
    'VAT',
    'Price',
  ];

  const rows = cartProperties.map(({ propertyId, property, items }) => {
    const rooms = getRoomQuantities(property);
    const row = {
      'ppty ID': propertyIdValue(property, propertyId),
      'ppty address': propertyAddressValue(property),
      'ppty postcode': postcodeValue(property),
      Area: areaValue(property),
      ...rooms,
    };

    let grossTotal = 0;

    items.forEach((item) => {
      const quantity = itemQuantity(item);
      const price = itemPrice(item);
      const fixedColumn = roomColumnForName(itemBaseName(item));

      if (fixedColumn && (row[fixedColumn] === '' || row[fixedColumn] === 0)) {
        row[fixedColumn] = quantity;
      } else if (!fixedColumn) {
        row[itemColumnName(item)] = quantity;
      }

      grossTotal += price * quantity;
    });

    const netTotal = grossTotal / (1 + VAT_RATE);
    row['Price without VAT'] = formatMoney(netTotal);
    row.VAT = formatMoney(grossTotal - netTotal);
    row.Price = formatMoney(grossTotal);

    return row;
  });

  const totalsRow = { 'ppty ID': 'TOTAL' };
  [...FIXED_ROOM_COLUMNS, ...dynamicItemColumns, 'Price without VAT', 'VAT', 'Price'].forEach((column) => {
    totalsRow[column] = formatMoney(rows.reduce((sum, row) => sum + toNumber(row[column]), 0));
  });

  const aoa = [
    headers,
    ...rows.map((row) => headers.map((header) => row[header] ?? '')),
    headers.map((header) => totalsRow[header] ?? ''),
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  worksheet['!cols'] = headers.map((header) => ({
    wch: Math.max(
      header.length + 2,
      ...aoa.slice(1).map((row) => String(row[headers.indexOf(header)] ?? '').length + 2),
    ),
  }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Cart');
  XLSX.writeFile(workbook, `cart_${new Date().toISOString().split('T')[0]}.xlsx`);

  return true;
}
